import { useState, useEffect } from 'react';
import { getAllFunds, getStreamsByFund, saveDDQTemplate } from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import FormField from '../../shared/Forms/FormField';
import SelectField from '../../shared/Forms/SelectField';
import Button from '../../shared/UI/Button';
import SectionBuilder from './SectionBuilder';

const BASE_SECTIONS = [
  {
    section_id: 's001',
    order: 1,
    title: 'Non-Disclosure Acknowledgement',
    description: 'Please read and acknowledge the Non-Disclosure Agreement before proceeding.',
    is_fixed: true,
    is_nda_section: true,
    visibility: 'shared',
    questions: [{
      question_id: 'q001',
      order: 1,
      question_text: 'I confirm that I have read and understood the Non-Disclosure Agreement and agree to its terms. Please type your company name below to confirm.',
      response_type: 'figure',
      weight: 0,
      is_required: true,
      allow_document_upload: false,
      options: [],
      figure_unit: 'Company Name',
      figure_score_bands: [],
    }],
  },
  { section_id: 's002', order: 2, title: 'Company Overview', description: '', is_fixed: true, is_nda_section: false, visibility: 'shared', questions: [] },
  { section_id: 's003', order: 3, title: 'Business Model and Strategy', description: '', is_fixed: true, is_nda_section: false, visibility: 'shared', questions: [] },
  { section_id: 's004', order: 4, title: 'Financial Overview', description: '', is_fixed: true, is_nda_section: false, visibility: 'shared', questions: [] },
  { section_id: 's005', order: 5, title: 'Management Team', description: '', is_fixed: true, is_nda_section: false, visibility: 'shared', questions: [] },
  { section_id: 's006', order: 6, title: 'Legal and Compliance', description: '', is_fixed: true, is_nda_section: false, visibility: 'shared', questions: [] },
  { section_id: 's007', order: 7, title: 'ESG and Impact', description: '', is_fixed: true, is_nda_section: false, visibility: 'shared', questions: [] },
  { section_id: 's008', order: 8, title: 'Internal Assessment', description: 'Internal analyst section — never visible to the company.', is_fixed: true, is_nda_section: false, visibility: 'internal', questions: [] },
];

// The company-type selector drives the s003/s004 product-vs-service branch
// (see the display_condition fields below and shouldShowSection in
// CompanyDSQPage). Its option_ids are FIXED semantic keys, deliberately not
// generated by makeOptId, because the branch logic matches on them. They are
// structural identifiers in the same sense as the dsq_s003_product section_id
// and the 'company_type:product' condition — the admin-editable configuration
// is option_text, which the resolver also accepts as a fallback so a
// hand-authored selector works on templates that pre-date this seed.
// weight 0 keeps the selector out of the score.
const COMPANY_TYPE_QUESTION = {
  question_id: 'dsq_company_type',
  order: 1,
  question_text: 'Is your company primarily a product company or a service company?',
  response_type: 'multiple_choice',
  weight: 0,
  is_required: true,
  allow_document_upload: false,
  options: [
    { option_id: 'product', option_text: 'Product', score: 0 },
    { option_id: 'service', option_text: 'Service', score: 0 },
    { option_id: 'both', option_text: 'Both', score: 0 },
  ],
  figure_unit: '',
  figure_score_bands: [],
};

const DSQ_BASE_SECTIONS = [
  { section_id: 'dsq_s001', order: 1, title: 'Overview and Governance', description: 'Company information, ownership structure, and governance', is_fixed: true, is_nda_section: false, visibility: 'shared', questions: [COMPANY_TYPE_QUESTION] },
  { section_id: 'dsq_s002', order: 2, title: 'Market', description: 'Market overview, size, competition, and positioning', is_fixed: true, is_nda_section: false, visibility: 'shared', questions: [] },
  { section_id: 'dsq_s003_product', order: 3, title: 'Products and Services (Product Company)', description: 'For product companies only', is_fixed: true, is_nda_section: false, visibility: 'shared', display_condition: 'company_type:product', questions: [] },
  { section_id: 'dsq_s003_service', order: 3, title: 'Products and Services (Service Company)', description: 'For service companies only', is_fixed: true, is_nda_section: false, visibility: 'shared', display_condition: 'company_type:service', questions: [] },
  { section_id: 'dsq_s004_product', order: 4, title: 'Operations (Product Company)', description: 'For product companies only', is_fixed: true, is_nda_section: false, visibility: 'shared', display_condition: 'company_type:product', questions: [] },
  { section_id: 'dsq_s004_service', order: 4, title: 'Operations (Service Company)', description: 'For service companies only', is_fixed: true, is_nda_section: false, visibility: 'shared', display_condition: 'company_type:service', questions: [] },
  { section_id: 'dsq_s005', order: 5, title: 'Growth Plan', description: 'Five-year growth plan and use of investment funds', is_fixed: true, is_nda_section: false, visibility: 'shared', questions: [] },
  { section_id: 'dsq_s006', order: 6, title: 'Internal Assessment', description: 'Analyst-only internal notes', is_fixed: true, is_nda_section: false, visibility: 'internal', questions: [] },
];

export default function DDQForm({ template, onSaved, onCancel, defaultTemplateType }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => {
    if (template) return { ...template };
    // New template: start on the type of the tab the admin clicked Add from,
    // falling back to 'ddq' when no tab type is supplied. The base sections
    // must follow the type, exactly as set('template_type') does below.
    const initialType = defaultTemplateType === 'dsq' ? 'dsq' : 'ddq';
    return {
      name: '', fund_id: '', stream_id: null,
      template_type: initialType,
      // New templates start unpublished — an admin publishes from the list.
      // The edit path above spreads the stored doc, so an already-published
      // template keeps its status when saved.
      status: 'draft',
      sections: initialType === 'dsq' ? DSQ_BASE_SECTIONS : BASE_SECTIONS,
    };
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [funds, setFunds] = useState([]);
  const [streams, setStreams] = useState([]);

  useEffect(() => {
    getAllFunds().then(list => setFunds(list.filter(f => f.is_active !== false)));
  }, []);

  useEffect(() => {
    if (!form.fund_id) { setStreams([]); return; }
    getStreamsByFund(form.fund_id).then(setStreams);
  }, [form.fund_id]);

  function set(field, value) {
    if (field === 'template_type' && !template) {
      const defaultSections = value === 'dsq' ? DSQ_BASE_SECTIONS : BASE_SECTIONS;
      setForm(prev => ({ ...prev, template_type: value, sections: defaultSections }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function validateStep1() {
    const e = {};
    if (!form.name?.trim()) e.name = 'Template name is required';
    if (!form.fund_id) e.fund_id = 'Fund is required';
    return e;
  }

  async function handleSave() {
    const e1 = validateStep1();
    if (Object.keys(e1).length) { setErrors(e1); setStep(1); return; }
    setSaving(true);
    try {
      await saveDDQTemplate({
        templateId: template?.id || null,
        templateData: { ...form, template_type: form.template_type || 'ddq' },
        performedBy: user.uid,
      });
      setSuccessMsg('Template saved successfully!');
      setTimeout(() => { setSuccessMsg(''); onSaved(); }, 2000);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  const fundOptions = funds.map(f => ({ value: f.id, label: f.name }));
  const streamOptions = [
    { value: '', label: 'Fund Level — No Stream' },
    ...streams.map(s => ({ value: s.id, label: s.name })),
  ];

  return (
    <div>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      <div className="stepper mb-4">
        <div className={`step ${step === 1 ? 'active' : 'completed'}`} onClick={() => setStep(1)} style={{ cursor: 'pointer' }}>
          1. Basic Details
        </div>
        <div className={`step ${step === 2 ? 'active' : ''}`} onClick={() => setStep(2)} style={{ cursor: 'pointer' }}>
          2. Sections ({(form.sections || []).length})
        </div>
      </div>

      {step === 1 && (
        <div className="card">
          <div className="card-header"><h2>Basic Details</h2></div>
          <div className="card-body">
            <div className="form-grid">
              <SelectField label="Template Type" value={form.template_type || 'ddq'}
                onChange={e => set('template_type', e.target.value)}
                disabled={!!template}
                options={[
                  { value: 'ddq', label: 'Due Diligence Questionnaire (DDQ)' },
                  { value: 'dsq', label: 'Deal Screening Questionnaire (DSQ)' },
                ]} />
              <FormField label="Template Name" required error={errors.name} className="form-full">
                <input className={`form-input ${errors.name ? 'error' : ''}`}
                  value={form.name} onChange={e => set('name', e.target.value)} />
              </FormField>
              <SelectField label="Fund" required error={errors.fund_id}
                value={form.fund_id} onChange={e => set('fund_id', e.target.value)}
                options={fundOptions} />
              <SelectField label="Stream" value={form.stream_id || ''}
                onChange={e => set('stream_id', e.target.value || null)}
                options={streamOptions} />
            </div>
          </div>
          <div className="card-footer">
            <Button variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button variant="primary" onClick={() => {
              const e = validateStep1();
              if (Object.keys(e).length) { setErrors(e); return; }
              setStep(2);
            }}>Next: Sections →</Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card">
          <div className="card-header">
            <h2>Section Builder</h2>
            <span className="text-muted" style={{ fontSize: 13 }}>
              Fixed sections cannot be removed. Add configurable sections below.
            </span>
          </div>
          <div className="card-body">
            <SectionBuilder sections={form.sections || []} onChange={s => set('sections', s)} />
          </div>
          <div className="card-footer">
            <Button variant="secondary" onClick={() => setStep(1)}>← Back</Button>
            <Button variant="primary" loading={saving} onClick={handleSave}>Save Template</Button>
          </div>
        </div>
      )}
    </div>
  );
}
